import React, { useState } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200">
      <button
        className="w-full py-6 flex justify-between items-center text-left"
        onClick={onClick}
      >
        <h3 className="text-lg font-semibold text-[#113262] pr-8">{question}</h3>
        <ChevronDown 
          className={`w-5 h-5 text-[#F49611] transform transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-gray-600">{answer}</p>
      </div>
    </div>
  );
};

const RootsAndWingsFaqs = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What is the 'Roots & Wings' investment philosophy?",
      answer: "Roots & Wings is our proprietary investment philosophy that guides our investment decisions. 'Roots' focuses on wealth preservation by selecting fundamentally strong companies with low debt, consistent ROE/ROCE, and high promoter integrity. 'Wings' aims to increase prosperity by identifying companies with significant growth potential in sales, profits, and cash flows. This balanced approach helps create sustainable long-term wealth."
    },
    {
      question: "How does the 'Roots' component help preserve wealth?",
      answer: "The 'Roots' component focuses on financial stability and strong fundamentals. We invest in businesses with very low debt, ensuring their growth is fueled by customers and internal accruals rather than leverage. We select companies that consistently demonstrate high Return on Equity (ROE) and Return on Capital Employed (ROCE), signaling efficient operations and shareholder-friendly management. Additionally, we prefer companies where promoters have significant ownership, demonstrating both 'skin-in-the-game' and 'soul-in-the-game'."
    },
    {
      question: "What does the 'Wings' component represent in your philosophy?",
      answer: "The 'Wings' component focuses on growth potential and prosperity. We look for companies with significant growth runways that typically grow 1.5 to 3 times faster than GDP. We prioritize businesses generating substantial operating cash flows, confirming their growth is real and sustainable. We also favor companies with strong pricing power and market dominance, which helps protect their growth trajectory even during challenging economic conditions."
    },
    {
      question: "How is machine learning incorporated into the Roots & Wings philosophy?",
      answer: "Our Roots & Wings philosophy is powered by machine learning technology that continuously optimizes portfolios by leveraging neural networks to map business parameters and stock patterns. Our algorithms analyze thousands of data points for each listed company, simulating millions of scenarios and testing for low drawdowns and faster recovery. This technology-driven approach combines human intelligence with rule-based heuristics and autonomous networks to create robustly diversified portfolios."
    },
    {
      question: "How do you implement the Roots & Wings philosophy in portfolio management?",
      answer: "We implement our Roots & Wings philosophy through a rigorous selection and monitoring process. We first identify companies that meet our strict Roots & Wings criteria. We then create optimally diversified portfolios across sectors, testing for low drawdowns and faster recovery. Our approach isn't static—we continuously monitor each investment through periodic reviews and make adjustments when early risk indicators appear. This disciplined process removes emotional decision-making and focuses on long-term wealth creation."
    }
  ];

  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
      <div className="flex flex-col md:flex-row gap-16">
        <div className="md:w-1/3">
          <div className="sticky top-8">
            <div className="w-12 h-0.5 bg-[#F49611] mb-3"></div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
              Understanding Roots & Wings
            </h2>
            <p className="text-gray-600 mb-8">
              Learn more about our unique investment philosophy that guides our portfolio selection and helps create sustainable long-term wealth for our clients.
            </p>
            <a href="/meeting" className="flex items-center text-[#F49611] font-semibold hover:gap-2 transition-all">
              <span>Speak With An Expert</span>
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
        <div className="md:w-2/3">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={index === openIndex}
              onClick={() => setOpenIndex(index === openIndex ? -1 : index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RootsAndWingsFaqs;